import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient } from '../../api';
import * as types from '../../api/types';
import { formatJobDate } from '../../utils/jobPresentation';
import { jobStatusLabel } from '../jobUi';
import { cellClass, DateField, EmptyRow, FilterGrid, Pager, RangeError, ReportCard, ReportPage, ReportStatus, ReportTable, SelectField, useReportData, useReportParams } from './ReportParts';
import { dateParam, dateRangeError, pageParam, REPORT_PAGE_SIZE, reportAssignee } from './reportUi';

const STATUSES = Object.values(types.JobStatus);
const STATUS_COLUMNS = [{ label: 'Status' }, { label: 'Jobs', numeric: true }];
const PROPERTY_COLUMNS = [{ label: 'Property' }, { label: 'Jobs', numeric: true }];
const JOB_COLUMNS = [{ label: 'Customer' }, { label: 'Property' }, { label: 'Status' }, { label: 'Scheduled' }, { label: 'Completed' }, { label: 'Assignee' }];

function usePropertyOptions(api: ReturnType<typeof createAPIClient>) {
  const [properties, setProperties] = useState<types.Property[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  useEffect(() => {
    let active = true;
    api.listProperties(undefined, page, 100).then((result) => {
      if (!active) return;
      setProperties((previous) => page === 1 ? result.data : [...previous, ...result.data.filter((property) => !previous.some((saved) => saved.id === property.id))]);
      setPages(Math.max(1, result.pagination.pages));
    }).catch(() => { /* Filter degrades to "All properties". */ });
    return () => { active = false; };
  }, [api, page]);
  return { properties, hasMore: page < pages, loadMore: () => setPage((current) => current + 1) };
}

function useAssigneeEmails(api: ReturnType<typeof createAPIClient>): Record<string, string> {
  const [emails, setEmails] = useState<Record<string, string>>({});
  useEffect(() => {
    let active = true;
    // Include inactive users so jobs assigned to someone since deactivated still show an email.
    api.listUsers(true).then((users) => {
      if (active) setEmails(Object.fromEntries(users.map((user) => [user.id, user.email])));
    }).catch(() => { /* Fall back to showing the user id. */ });
    return () => { active = false; };
  }, [api]);
  return emails;
}

export function JobReportPage() {
  const api = useMemo(() => createAPIClient(), []);
  const { properties, hasMore, loadMore } = usePropertyOptions(api);
  const emails = useAssigneeEmails(api);
  const { params, change } = useReportParams();
  const startDate = dateParam(params.get('startDate'));
  const endDate = dateParam(params.get('endDate'));
  const status = params.get('status') || '';
  const propertyId = params.get('propertyId') || '';
  const page = pageParam(params.get('page'));
  const rangeError = dateRangeError(startDate, endDate);
  const query = { startDate, endDate, status, propertyId, page, limit: REPORT_PAGE_SIZE };
  const state = useReportData(
    rangeError ? null : JSON.stringify(query),
    () => api.getJobReport(query),
    'Could not load the job report',
  );

  return (
    <ReportPage
      title="Jobs"
      intro="Job counts by status and by property, with the job list. The date range filters on scheduled date, so unscheduled jobs only appear when no dates are set."
    >
      <FilterGrid>
        <DateField id="jobs-start" label="Scheduled from" value={startDate} onChange={(value) => change({ startDate: value })} />
        <DateField id="jobs-end" label="Scheduled to" value={endDate} onChange={(value) => change({ endDate: value })} />
        <SelectField id="jobs-status" label="Status" value={status} onChange={(value) => change({ status: value })}>
          <option value="">All statuses</option>
          {STATUSES.map((option) => <option key={option} value={option}>{jobStatusLabel(option)}</option>)}
        </SelectField>
        <SelectField
          id="jobs-property"
          label="Property"
          value={propertyId}
          onChange={(value) => change({ propertyId: value })}
          footer={hasMore && <button type="button" className="mt-1 py-2.5 sm:py-0 text-sm text-blue-700 underline" onClick={loadMore}>Load more properties</button>}
        >
          <option value="">All properties</option>
          {propertyId && !properties.some((property) => property.id === propertyId) && <option value={propertyId}>Selected property</option>}
          {properties.map((property) => <option key={property.id} value={property.id}>{property.address}{property.city ? `, ${property.city}` : ''}</option>)}
        </SelectField>
      </FilterGrid>
      <RangeError message={rangeError} />
      <ReportStatus state={state} label="job report">
        {(report) => (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <ReportCard title="By status">
                <ReportTable columns={STATUS_COLUMNS} caption="Jobs by status">
                  {report.byStatus.length === 0 ? <EmptyRow colSpan={2} message="No jobs match these filters." /> : report.byStatus.map((row) => (
                    <tr key={row.status}>
                      <td className={cellClass()} data-label="Status" data-primary>
                        <button type="button" className="text-blue-700 hover:underline text-left" onClick={() => change({ status: row.status })}>{jobStatusLabel(row.status)}</button>
                      </td>
                      <td className={cellClass(true)} data-label="Jobs">{row.count}</td>
                    </tr>
                  ))}
                </ReportTable>
              </ReportCard>
              <ReportCard title="By property">
                <ReportTable columns={PROPERTY_COLUMNS} caption="Jobs by property">
                  {report.byProperty.length === 0 ? <EmptyRow colSpan={2} message="No jobs match these filters." /> : report.byProperty.map((row) => (
                    <tr key={row.propertyId}>
                      <td className={cellClass()} data-label="Property" data-primary>
                        <button type="button" className="text-blue-700 hover:underline text-left break-words" onClick={() => change({ propertyId: row.propertyId })}>{row.address}</button>
                      </td>
                      <td className={cellClass(true)} data-label="Jobs">{row.count}</td>
                    </tr>
                  ))}
                </ReportTable>
              </ReportCard>
            </div>
            <ReportCard title="Job list">
              <ReportTable columns={JOB_COLUMNS} caption="Jobs">
                {report.data.length === 0 ? <EmptyRow colSpan={JOB_COLUMNS.length} message="No jobs match these filters." /> : report.data.map((job) => (
                  <tr key={job.id}>
                    <td className={cellClass()} data-label="Customer" data-primary><Link to={`/jobs/${job.id}`} className="font-semibold text-blue-700 hover:underline">{job.customerName}</Link></td>
                    <td className={`${cellClass()} break-words`} data-label="Property">{job.propertyAddress}</td>
                    <td className={cellClass()} data-label="Status">{jobStatusLabel(job.status)}</td>
                    <td className={cellClass()} data-label="Scheduled">{formatJobDate(job.scheduledDate)}</td>
                    <td className={cellClass()} data-label="Completed">{job.completedDate ? formatJobDate(job.completedDate) : '—'}</td>
                    <td className={`${cellClass()} break-all md:break-normal`} data-label="Assignee">{reportAssignee(job.assignedTo, emails)}</td>
                  </tr>
                ))}
              </ReportTable>
              <Pager pagination={report.pagination} noun="job" onPage={(next) => change({ page: next > 1 ? String(next) : '' })} />
            </ReportCard>
          </>
        )}
      </ReportStatus>
    </ReportPage>
  );
}
